
<template>
  <div>
    <dialog-loading loadingText="Salvando..." v-if="saving"></dialog-loading>
    <DialogResumePayment
      ref="resume_payment"
      v-if="new_payment.client_id"
      @submitPayment="preparePayment"
      :selected_vrts="selected_vrts_data.selected_vrts"
      :payment_ammount="payment_total"
      :bonus_ammount="Math.max(0, bonus_ammount)"
      :add_bonus="to_generate_bonus && !is_bonus_payment"
    ></DialogResumePayment>

    <v-dialog v-model="dialog" scrollable max-width="1100px" persistent transition="dialog-bottom-transition">
      <v-card class="charge-dialog-card">
        <div class="charge-dialog-header" :class="`charge-dialog-header--${meta.header}`">
          <div class="d-flex align-center">
            <v-avatar color="white" size="40" class="mr-3 elevation-1">
              <v-icon :color="meta.color">{{ meta.icon }}</v-icon>
            </v-avatar>
            <div>
              <div class="headline white--text mb-0">{{ meta.title }}</div>
              <div class="caption white--text" style="opacity:.85">{{ meta.subtitle }}</div>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="close()"><v-icon>mdi-close</v-icon></v-btn>
          </div>
        </div>

        <v-card-text class="pa-5">
          <v-form ref="paymentForm" lazy-validation>
            <v-row dense class="mb-2">
              <v-col cols="12" sm="5">
                <SelectModality
                  :object_with_model="new_payment"
                  custom_label="Módulo"
                  outlined
                  dense
                  preselect_active
                />
              </v-col>
              <v-col cols="12" sm="7">
                <v-autocomplete
                  v-model="new_payment.client_id"
                  :items="all_clients"
                  :item-text="formatClientDisplayName"
                  item-value="id"
                  label="Cliente"
                  prepend-inner-icon="mdi-account"
                  :loading="loading_clients"
                  :readonly="loading_clients"
                  :disabled="!new_payment.place_id"
                  :hint="!new_payment.place_id ? 'Selecione o módulo primeiro' : ''"
                  persistent-hint
                  :rules="required"
                  :filter="filterClients"
                  outlined
                  dense
                  hide-details="auto"
                >
                  <template v-slot:selection="{ item }">
                    <span class="text-truncate">
                      {{ formatClientDisplayName(item) }}
                      <span v-if="item.fantasyName" class="grey--text">({{ item.fantasyName }})</span>
                    </span>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title class="pb-1">Nome/Rz social: <b>{{ formatClientDisplayName(item) }}</b></v-list-item-title>
                      <v-list-item-subtitle class="mb-1" v-if="item.fantasyName && item.fantasyName != item.name">Nome fantasia: <b>{{ item.fantasyName }}</b></v-list-item-subtitle>
                      <v-list-item-subtitle class="d-flex justify-space-between">
                        <div>ID: <b>{{ item.id }}</b></div>
                        <div>Cidade: <b>{{ item.city }}</b></div>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <div class="charge-section-title mt-3">{{ meta.section }}</div>
            <with-deposit
              v-if="new_payment.kind == 'deposit'"
              :payment="new_payment"
              @doneDoc="handleDoneDoc"
              @removeDoc="handleRemoveDoc"
            ></with-deposit>
            <with-envelope v-if="new_payment.kind == 'envelope'" :payment="new_payment"></with-envelope>
            <with-bonus
              v-if="new_payment.kind == 'bonus'"
              :client_bonus="client_bonus"
              :client_id="new_payment.client_id"
              :payment="new_payment"
            ></with-bonus>

            <v-row dense class="mt-4 mb-3">
              <v-col cols="12" sm="4">
                <div class="charge-summary-card d-flex align-center">
                  <v-avatar size="40" color="green lighten-5" class="mr-3">
                    <v-icon color="green darken-2">mdi-cash-check</v-icon>
                  </v-avatar>
                  <div>
                    <div class="caption grey--text text--darken-1">
                      {{ new_payment.envelope_details.has_check ? 'Faturas / Total em cheques' : 'Faturas / Pagamento' }}
                    </div>
                    <div v-if="!has_set_payment && checks_payment_total <= 0" class="body-2 font-italic error--text">Informe o valor do pagamento</div>
                    <div v-else-if="!has_set_payment && checks_payment_total > 0" class="subtitle-1 font-weight-bold">
                      <span class="caption font-weight-regular grey--text">Total em cheques: </span>{{ formatMoney(checks_payment_total) }}
                    </div>
                    <div v-else-if="!vrts_is_selected" class="body-2 font-italic" :class="to_generate_bonus && !is_bonus_payment ? 'grey--text' : 'error--text'">
                      {{ to_generate_bonus && !is_bonus_payment ? 'Nenhuma fatura: tudo vira bônus' : 'Selecione as faturas abaixo' }}
                    </div>
                    <div v-else class="subtitle-1 font-weight-bold">
                      <span :class="{ 'error--text': !paying_vrts_ammount_is_valid || !bonus_payment_is_valid }">{{ formatMoney(paying_vrts_ammount) }}</span>
                      <span class="caption font-weight-regular"> / {{ formatMoney(payment_total) }}</span>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="4" v-if="is_bonus_payment">
                <div class="charge-summary-card d-flex align-center">
                  <v-avatar size="40" color="purple lighten-5" class="mr-3">
                    <v-icon color="purple darken-2">mdi-gift-outline</v-icon>
                  </v-avatar>
                  <div>
                    <div class="caption grey--text text--darken-1">Bônus disponível do cliente</div>
                    <div v-if="new_payment.client_id" class="subtitle-1 font-weight-bold">{{ formatMoney(client_bonus) }}</div>
                    <div v-else class="body-2 font-italic error--text">Selecione o cliente</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="4" v-else>
                <div class="charge-summary-card d-flex align-center">
                  <v-avatar size="40" color="teal lighten-5" class="mr-3">
                    <v-icon color="teal darken-2">mdi-cash-multiple</v-icon>
                  </v-avatar>
                  <div>
                    <div class="caption grey--text text--darken-1">Bônus sendo gerado</div>
                    <div v-if="to_generate_bonus && has_set_payment" class="subtitle-1 font-weight-bold">{{ formatMoney(Math.max(0, bonus_ammount)) }}</div>
                    <div v-else class="body-2 font-italic grey--text">{{ to_generate_bonus ? '—' : 'Desativado' }}</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="charge-summary-card d-flex align-center">
                  <v-avatar size="40" color="red lighten-5" class="mr-3">
                    <v-icon color="red darken-2">mdi-book-open-variant-outline</v-icon>
                  </v-avatar>
                  <div>
                    <div class="caption grey--text text--darken-1">Em aberto do cliente</div>
                    <div class="subtitle-1 font-weight-bold">{{ formatMoney(selected_vrts_data.total_debt) }}</div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <div
              v-if="has_set_payment && vrts_is_selected"
              class="charge-sum-box mb-3"
              :class="sum_ok ? 'charge-sum-box--ok' : 'charge-sum-box--err'"
            >
              <div class="d-flex justify-space-between align-center mb-1">
                <span class="caption font-weight-medium">
                  Faturas cobertas pelo pagamento
                  <span v-if="!sum_ok" class="error--text"> · {{ sum_error_message }}</span>
                </span>
                <span class="subtitle-2 font-weight-bold">
                  {{ formatMoney(paying_vrts_ammount) }}
                  <span class="caption font-weight-regular"> / {{ formatMoney(payment_total) }}</span>
                </span>
              </div>
              <v-progress-linear
                :value="sumProgress"
                height="6"
                rounded
                :color="sum_ok ? 'success' : 'warning'"
              ></v-progress-linear>
            </div>

            <v-switch
              v-if="!is_bonus_payment"
              v-model="to_generate_bonus"
              inset
              dense
              color="teal darken-1"
              class="mt-0 mb-2"
              hide-details
              label="Gerar bônus com a diferença entre o pagamento e as faturas"
            ></v-switch>

            <VrtSelection
              v-if="new_payment.client_id"
              :payment_total="payment_total"
              :client_id="new_payment.client_id"
              @onChange="handleChangeVrtSelection"
            ></VrtSelection>
            <div v-else class="charge-empty-box text-center pa-6">
              <v-icon size="40" color="blue-grey lighten-3">mdi-account-search-outline</v-icon>
              <div class="body-2 grey--text mt-2">Escolha um cliente para exibir as faturas.</div>
            </div>

            <template v-if="new_payment.kind == 'deposit'">
              <div class="charge-section-title mt-5">Comprovante</div>
              <FileAttachment ref="file_attachment" :update_infos="{MODEL: 'Deposit', id: new_payment_id}" :is_creating="true"></FileAttachment>
            </template>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text :disabled="saving" @click="close()">Cancelar</v-btn>
          <v-btn
            :color="meta.color"
            depressed
            class="px-5 white--text"
            :disabled="!payment_ready"
            @click="showResume()"
          >
            <v-icon left small>mdi-check</v-icon>
            {{ meta.action }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Api from '@/api/index';
import validations from '@/mixins/validations'
import status from "@/_backoffice/_financial/mixins/status.js"
import clientDisplay from '@/mixins/clientDisplay'

const KIND_META = {
  deposit: {
    title: 'Receber Depósito',
    subtitle: 'Registre o depósito e selecione as faturas que ele paga',
    section: 'Dados do depósito',
    action: 'Receber depósito',
    icon: 'mdi-bank-transfer-in',
    header: 'deposit',
    color: 'blue darken-2',
  },
  cash: {
    title: 'Receber Dinheiro',
    subtitle: 'Registre o valor recebido em espécie e selecione as faturas',
    section: 'Valor recebido',
    action: 'Receber dinheiro',
    icon: 'mdi-cash-multiple',
    header: 'cash',
    color: 'green darken-2',
  },
  check: {
    title: 'Receber Cheque',
    subtitle: 'Leia os CMC7, complete os dados dos cheques e selecione as faturas',
    section: 'Cheques',
    action: 'Receber cheques',
    icon: 'mdi-checkbook',
    header: 'check',
    color: 'deep-orange darken-2',
  },
  bonus: {
    title: 'Pagar com Bônus',
    subtitle: 'Use o bônus disponível do cliente para quitar faturas',
    section: 'Valor em bônus',
    action: 'Pagar com bônus',
    icon: 'mdi-gift-outline',
    header: 'bonus',
    color: 'purple darken-2',
  },
}

export default {
  name: 'NewPayment',
  mixins: [ validations, status, clientDisplay ],
  components: {
    SelectModality: () => import('@/components/shared/SelectModality.vue'),
    WithDeposit: ()=> import('./WithDeposit'),
    WithEnvelope: ()=> import('./WithEnvelope'),
    WithBonus: ()=> import('./WithBonus'),
    FileAttachment: () => import('@/components/shared/FileAttachment.vue'),
    VrtSelection: () => import("@/_backoffice/_financial/components/order_payment/VrtSelection.vue"),
    DialogResumePayment: () => import("@/_backoffice/_financial/components/order_payment/DialogResumePayment.vue"),
  },
  props:{
    kind: {
      type: String,
      default: "deposit"
    },
    envelope_type: {
      type: String,
      default: "cash"
    },
  },
  data() {
    return {
      dialog: true,
      saving: false,
      to_generate_bonus: false,
      loading_clients: false,
      all_clients: [],
      selected_vrts_data:{
        paying_vrts: [],
        selected_vrts: [],
        selected_vrts_ammount: '0.00',
        total_debt: '0.00',
      },
      new_payment: {
        client_id: null,
        kind: null,
        payed_at: new Date().toISOString().substring(0,10), 
        status_id: 7,
        total_value: '0',
        place_id: null,
        deposit_details: {
          has_entrance: false,
          entrance_value_aux: '0.00',
          has_one_day: false,
          one_day_value_aux: '0.00',
          has_two_days: false,
          two_days_value_aux: '0.00',
          deposit_date: null,
          deposit_bank: null,
          deposit_company: null,
          comment: "",
          document: null,
        },
        envelope_details: {
          has_cash: false,
          in_cash_aux: '0.00',
          has_check: false,
          checks_attributes: []
        },
      },
      new_payment_id: null,
    }
  },
  computed: {
    meta() {
      const key = this.kind == 'envelope' ? this.envelope_type : this.kind
      return KIND_META[key] || KIND_META.deposit
    },
    client_bonus(){
      if(this.is_bonus_payment){
        return Number(this.all_clients.find(client => client.id == this.new_payment.client_id)?.bonus || 0)
      }
      return 0
    }, 
    paying_vrts_ammount_is_valid(){
      if(this.to_generate_bonus){
        return Number(this.paying_vrts_ammount) <= this.payment_total + 0.001
      }
      return Number(this.paying_vrts_ammount).toFixed(2) === this.payment_total.toFixed(2)
    },
    vrts_is_selected(){
      return this.selected_vrts_data.paying_vrts.length > 0
    },
    paying_vrts_ammount(){
      return this.selected_vrts_data.paying_vrts.reduce((sum, vrt) => {
        return sum + (Number(vrt.paying_ammount) || 0) + (Number(vrt.paying_juros) || 0)
      }, 0).toFixed(2)
    },
    has_set_payment(){
      return this.payment_total > 0
    },
    checks_payment_total() {
      if (!this.new_payment.envelope_details?.has_check) return 0
      const total = this.new_payment.envelope_details.checks_attributes.reduce(
        (sum, check) => sum + this.parseMoney(check.value_aux),
        0
      )
      return Number(total.toFixed(2))
    },
    bonus_ammount(){
      return Number((this.payment_total - Number(this.paying_vrts_ammount)).toFixed(2))
    },
    is_bonus_payment(){
      return this.new_payment.kind == 'bonus'
    },
    bonus_payment_is_valid(){
      // Se for pagamento de bonus não pode sobrar valor
      if (!this.is_bonus_payment) return true
      return Number(this.paying_vrts_ammount).toFixed(2) === this.payment_total.toFixed(2)
        && this.payment_total <= this.client_bonus + 0.001
    },
    sum_ok() {
      return this.paying_vrts_ammount_is_valid && this.bonus_payment_is_valid
    },
    sum_error_message() {
      if (this.is_bonus_payment && this.payment_total > this.client_bonus + 0.001) return 'valor maior que o bônus disponível'
      if (Number(this.paying_vrts_ammount) > this.payment_total + 0.001) return 'faturas maiores que o pagamento'
      return 'o valor das faturas tem que ser igual ao pagamento (ou ative o bônus)'
    },
    sumProgress() {
      if (this.payment_total <= 0) return 0
      return Math.min(100, (Number(this.paying_vrts_ammount) / this.payment_total) * 100)
    },
    payment_total() {
      let total = 0
      if (this.new_payment.kind == 'deposit'){
        const deposit = this.new_payment.deposit_details
        if (deposit.has_entrance) total += this.parseMoney(deposit.entrance_value_aux)
        if (deposit.has_one_day) total += this.parseMoney(deposit.one_day_value_aux)
        if (deposit.has_two_days) total += this.parseMoney(deposit.two_days_value_aux)
      }else if (this.new_payment.kind == 'envelope'){
        const envelope = this.new_payment.envelope_details
        if (envelope.has_cash) total += this.parseMoney(envelope.in_cash_aux)
        if (envelope.has_check) total += envelope.checks_attributes.reduce((sum, check) => sum + this.parseMoney(check.value_aux), 0)
      }else{
        total = this.parseMoney(this.new_payment.total_value)
      }
      return Number(total.toFixed(2))
    },
    kind_fields_ok() {
      if (this.new_payment.kind == 'deposit') {
        const deposit = this.new_payment.deposit_details
        const selected_deposit_values = [
          { selected: deposit.has_entrance, value: deposit.entrance_value_aux },
          { selected: deposit.has_one_day, value: deposit.one_day_value_aux },
          { selected: deposit.has_two_days, value: deposit.two_days_value_aux },
        ].filter(deposit_value => deposit_value.selected)
        return selected_deposit_values.length > 0
          && selected_deposit_values.every(deposit_value => this.parseMoney(deposit_value.value) > 0)
          && !!deposit.deposit_date && !!deposit.deposit_bank && !!deposit.deposit_company
      }
      if (this.new_payment.kind == 'envelope') {
        const envelope = this.new_payment.envelope_details
        if (envelope.has_cash) return this.parseMoney(envelope.in_cash_aux) > 0
        if (envelope.has_check) {
          return envelope.checks_attributes.length > 0 && envelope.checks_attributes.every(check =>
            this.parseMoney(check.value_aux) > 0 && check.cmc7 && check.bank && check.agency
            && check.account_number && check.account_digit && check.check_number && check.nominal && check.good_to
          )
        }
        return false
      }
      if (this.is_bonus_payment) return this.payment_total <= this.client_bonus + 0.001
      return true
    },
    payment_ready() {
      if (this.saving || !this.new_payment.place_id || !this.new_payment.client_id) return false
      if (!this.has_set_payment || !this.kind_fields_ok) return false
      if (this.is_bonus_payment) return this.vrts_is_selected && this.bonus_payment_is_valid
      if (!this.to_generate_bonus && !this.vrts_is_selected) return false
      return this.paying_vrts_ammount_is_valid
    },
  },
  methods: {
    parseMoney(value) {
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0
      const raw = String(value ?? '').trim()
      if (!raw) return 0
      const hasComma = raw.includes(',')
      const hasDot = raw.includes('.')
      if (hasComma && hasDot) {
        if (raw.lastIndexOf(',') > raw.lastIndexOf('.')) {
          return Number(raw.replace(/\./g, '').replace(',', '.')) || 0
        }
        return Number(raw.replace(/,/g, '')) || 0
      }
      if (hasComma) return Number(raw.replace(/\./g, '').replace(',', '.')) || 0
      return Number(raw) || 0
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    close() {
      this.dialog = false
    },
    setPaymentKind(){
      if(this.envelope_type == 'cash'){
        this.new_payment.envelope_details.has_cash = true
      }else if(this.envelope_type == 'check'){
        this.new_payment.envelope_details.has_check = true
      }
      this.new_payment.kind = this.kind
    },
    isFinalClient (client) {
      const label = `${client?.name || ''} ${client?.corporateName || ''}`.toLowerCase()
      return label.includes('cliente final')
    },
    filterClients (item, queryText) {
      const name = item.name ? item.name.toLowerCase() : ''
      const corporate_name = item.corporateName ? item.corporateName.toLowerCase() : ''
      const fantasyName = item.fantasyName ? item.fantasyName.toLowerCase() : ''
      const city = item.city ? item.city.toLowerCase() : ''
      const id = item.id ? item.id.toString() : ''
      const searchText = queryText.toLowerCase()

      return name.indexOf(searchText) > -1 || corporate_name.indexOf(searchText) > -1 || fantasyName.indexOf(searchText) > -1 || city.indexOf(searchText) > -1 || id.indexOf(searchText) > -1
    },
    resetVrtSelection() {
      this.selected_vrts_data = {
        paying_vrts: [],
        selected_vrts: [],
        selected_vrts_ammount: '0.00',
        total_debt: '0.00',
      }
    },
    getAllClients() {
      if (!this.new_payment.place_id) return
      this.new_payment.client_id = null
      this.resetVrtSelection()
      this.loading_clients = true
      let filter = {modality_id: this.new_payment.place_id, with_bonus: true, short: true}

      Api.Client.index(filter).then(response => response.data).then(body => {
        const data = body.data ? body.data.map(client => client.attributes) : body
        const clients = Array.isArray(data) ? data : []
        this.all_clients = ['bonus', 'cash', 'envelope'].includes(this.new_payment.kind)
          ? clients
          : clients.filter(client => !this.isFinalClient(client))
      }).catch(err=>{
        console.log(err);
      }).finally(()=>{
        this.loading_clients = false
      })
    },
    handleChangeVrtSelection( data ){
      this.selected_vrts_data = {...data}
    },
    handleDoneDoc(e) {
      this.new_payment = {
        ...this.new_payment,
        deposit_details: {
          ...this.new_payment.deposit_details,
          document: {...e}
        }
      }
    },
    handleRemoveDoc(){
      this.new_payment = {
        ...this.new_payment,
        deposit_details: {
          ...this.new_payment.deposit_details,
          document: {}
        }
      }
    },
    showResume(){
      if (this.$refs.paymentForm.validate() && this.payment_ready) {
        this.$refs.resume_payment.openDialog()
      }else{
        this.addNotification({ type: 'error', msg: 'Verifique todos os campos!' })
      }
    },
    preparePayment(){
      this.$swal({
        showConfirmButton: true,
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        title: `Receber pagamento?`,
        icon: 'question',
      }).then(r=>{
        if (r.isConfirmed) {
          this.new_payment.status_id = this.status_id_map['Payed']
          const {client_id, kind, payed_at, status_id} = this.new_payment
          let pay = {
            client_id, kind, payed_at, status_id,
            total_value: this.payment_total,
            vrt_payments_attributes: this.selected_vrts_data.paying_vrts,
            place_id: this.new_payment.place_id,
          }
          
          if (this.new_payment.kind == 'deposit'){
            pay.deposit_attributes = this.generateDepositAttributes()
          }
          if (this.new_payment.kind == 'bonus'){
            pay.bonus_attributes = this.generateBonusAttributes()
          }
          if (this.new_payment.kind == 'envelope'){
            if (this.new_payment.envelope_details.has_check){
              pay.checks_attributes = this.generateChecksAttributes()
              pay.check_value = Number(pay.checks_attributes.reduce((acc, one) => acc + Number(one.value), 0).toFixed(2))
            }
            if (this.new_payment.envelope_details.has_cash){
              pay.cash_attributes = this.generateCashAttributes()
              pay.cash_value = (pay.cash_attributes && pay.cash_attributes.value) ? pay.cash_attributes.value : null
            }
          }
          this.submitPayment(pay)
        }
      })
    },
    generateDepositAttributes(){
      const { deposit_date, deposit_bank, deposit_company, document,
        has_entrance, entrance_value_aux,
        has_one_day, one_day_value_aux,
        has_two_days, two_days_value_aux, comment,
      } = this.new_payment.deposit_details

      return {
        value: this.payment_total,
        entrance_value: has_entrance? this.parseMoney(entrance_value_aux) : null,
        one_day_value: has_one_day? this.parseMoney(one_day_value_aux) : null,
        two_days_value: has_two_days? this.parseMoney(two_days_value_aux) : null,
        deposit_date,
        deposit_bank,
        deposit_company,
        comment,
        client_id: this.new_payment.client_id, 
        deposited: false,
        place_id: this.new_payment.place_id,
        status_id: this.new_payment.status_id,
        payed_at: this.new_payment.payed_at, 
        document: (document && document.url_document)? document : null,
      }
    },
    generateChecksAttributes(){
      return this.new_payment.envelope_details.checks_attributes.map(check => {
        const {value_aux, client_name, cnpj_account, nominal_cpf, nominal_cnpj, ...check_fields} = check
        return {
          ...check_fields,
          id: null,
          nominal_cpf: cnpj_account? null : nominal_cpf ,
          nominal_cnpj: cnpj_account? nominal_cnpj : null ,
          value: this.parseMoney(value_aux),
          client_id: this.new_payment.client_id,
          status_id: this.new_payment.status_id,
          payed_at: this.new_payment.payed_at,
          place_id: this.new_payment.place_id,
        }
      })
    },
    generateCashAttributes(){
      const { has_cash, in_cash_aux } = this.new_payment.envelope_details
      if (!has_cash) return null
      return {
        value: this.parseMoney(in_cash_aux),
        place_id: this.new_payment.place_id,
        client_id: this.new_payment.client_id,
        status_id: this.new_payment.status_id,
        payed_at: this.new_payment.payed_at,
      }
    },
    generateBonusAttributes(){
      return {
        value: this.payment_total,
        client_id: this.new_payment.client_id,
        comment: `Pagamento em bônus de R$${this.payment_total.toFixed(2)}`
      }
    },
    submitPayment(payment){
      this.saving = true
      Api.Payment.create(payment).then(r=>r.data).then(r=> {
        if(this.new_payment.kind == 'deposit'){
          let depositId = r?.data?.attributes?.deposit?.data?.attributes?.id
            || r?.data?.attributes?.deposit?.data?.id
          if (depositId) {
            this.$refs.file_attachment?.updateFile(depositId)
          }
        }
        this.successSaved()
        this.$emit('done', r)
        this.close()
      }).catch(err=>{
        if(err?.response?.data?.errors?.[0] == "Checks cmc7 has already been taken"){
          this.addNotification({type: 'error',msg: `Erro: Esse cheque já foi cadastrado.`})
        }else{
          this.errorCreated()
        }
      }).finally(()=>{
        this.saving = false
      })
    },
  },
  mounted () {
    this.setPaymentKind()
  },
  watch: {
    dialog(nv) {
      if (nv === false) this.$emit('close')
    },
    'new_payment.place_id'(nv, ov){
      if(nv && nv > 0 && ov != nv){
        this.getAllClients();
      }
    },
    'new_payment.client_id'() {
      this.resetVrtSelection()
    },
  },
}
</script>

<style src="../../styles/charge_dialog.css"></style>
